import { IAppContext } from 'App/AppContext';
import { Role } from 'models/users/role';
import { Settings } from 'models/tenants/setting';
import User from 'models/users/user';
import { UserSettings } from 'models/users/userSetting';
import { EntityCache } from './entityCache';

//
// The reference data of the organization the app keeps for the session: the settings, the users and the
// roles. The login answer fills it (GET users/login, globalDataCache); a refresh signs the profile in again.
//
export class UserCache extends EntityCache<User> {
  constructor() {
    super(
      (user) => user.id,
      (id) => User.getEmptyUser(id as string | undefined),
    );
  }

  clone(): UserCache {
    const userCache = new UserCache();
    userCache.items = this.items.map((u) => u.clone());

    return userCache;
  }
}

export class RoleCache extends EntityCache<Role> {
  constructor() {
    super(
      (role) => role.roleId,
      (id) => Role.getEmptyRole(id as string | undefined),
    );
  }

  getForSystemId(systemRoleId: number): Role | undefined {
    return this.items.find((role) => role.systemRoleId === systemRoleId);
  }

  clone(): RoleCache {
    const roleCache = new RoleCache();
    roleCache.items = this.items.map((r) => r.clone());

    return roleCache;
  }
}

export default class GlobalDataCache {
  appContext?: IAppContext;

  settings: Settings;

  //the signed-in user's own preferences; only the stored ones, so UserSettings.get falls back to the default
  userSettings: UserSettings;

  users: UserCache;

  roles: RoleCache;

  constructor() {
    this.settings = new Settings();
    this.userSettings = new UserSettings();
    this.users = new UserCache();
    this.roles = new RoleCache();
  }

  setAppContext(appContext: IAppContext) {
    this.appContext = appContext;
    this.users.appContext = appContext;
    this.roles.appContext = appContext;
    this.users.cacheRef = this;
    this.roles.cacheRef = this;
  }

  //the reference data comes with the login answer: reloading the profile refreshes it
  async refresh() {
    if (this.appContext) {
      await this.appContext.setUserProfile();
    }
  }

  clone(): GlobalDataCache {
    const newGlobalDataCache = new GlobalDataCache();
    newGlobalDataCache.settings = this.settings.clone();
    newGlobalDataCache.userSettings = this.userSettings.clone();
    newGlobalDataCache.users = this.users.clone();
    newGlobalDataCache.roles = this.roles.clone();

    if (this.appContext) {
      newGlobalDataCache.setAppContext(this.appContext);
    }

    return newGlobalDataCache;
  }

  clear() {
    this.settings = new Settings();
    this.userSettings = new UserSettings();
    this.users = new UserCache();
    this.roles = new RoleCache();
  }
}
